import * as React from 'react';
import { cn } from '../../lib/utils';
import { type TestIdProps, testIdProps } from '../../lib/test-id';
import { Button, type ButtonProps } from '../atoms/button';

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement>, TestIdProps {
  orientation?: 'horizontal' | 'vertical';
  segmented?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
}

type ButtonGroupContextValue = {
  segmented?: boolean;
  orientation?: 'horizontal' | 'vertical';
  value?: string;
  onValueChange?: (value: string) => void;
};

const ButtonGroupContext = React.createContext<ButtonGroupContextValue>({});

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    {
      className,
      orientation = 'horizontal',
      segmented = false,
      value,
      onValueChange,
      children,
      testId,
      ...props
    },
    ref,
  ) => (
    <ButtonGroupContext.Provider value={{ segmented, orientation, value, onValueChange }}>
      <div
        ref={ref}
        role={segmented ? 'group' : undefined}
        className={cn(
          'inline-flex overflow-hidden rounded-md border border-border bg-canvas',
          orientation === 'vertical' ? 'flex-col' : 'flex-row',
          className,
        )}
        {...props}
        {...testIdProps(testId)}
      >
        {children}
      </div>
    </ButtonGroupContext.Provider>
  ),
);
ButtonGroup.displayName = 'ButtonGroup';

export type ButtonGroupItemProps = ButtonProps & {
  value?: string;
  testId?: string;
};

function itemDividerClass(vertical: boolean) {
  if (vertical) {
    return 'border-b border-b-border last:border-b-0';
  }
  return 'border-l border-l-border first:border-l-0';
}

function itemSegmentedClass(segmented: boolean, selected: boolean) {
  if (!segmented) return;
  if (selected) return;
  return 'bg-transparent text-foreground hover:bg-hover';
}

function resolveItemVariant(
  segmented: boolean,
  selected: boolean,
  variant: ButtonProps['variant'],
) {
  if (!segmented) return variant ?? 'default';
  if (selected) return 'primary';
  return 'ghost';
}

const ButtonGroupItem = React.forwardRef<HTMLButtonElement, ButtonGroupItemProps>(
  ({ className, value, variant, onClick, testId, ...props }, ref) => {
    const ctx = React.useContext(ButtonGroupContext);
    const selected = Boolean(ctx.segmented && value != null && ctx.value === value);
    const vertical = ctx.orientation === 'vertical';
    const segmented = Boolean(ctx.segmented);

    return (
      <Button
        ref={ref}
        {...props}
        variant={resolveItemVariant(segmented, selected, variant)}
        className={cn(
          'rounded-none border-0 shadow-none',
          itemSegmentedClass(segmented, selected),
          itemDividerClass(vertical),
          className,
        )}
        aria-pressed={segmented ? selected : undefined}
        onClick={(e) => {
          onClick?.(e);
          if (value != null) ctx.onValueChange?.(value);
        }}
        data-element-id={testId}
      />
    );
  },
);
ButtonGroupItem.displayName = 'ButtonGroupItem';

export { ButtonGroup, ButtonGroupItem };
